import {
    AUTO_SAVE_FAIL,
    AUTO_SAVE_SUCCESS,
    ENGAGEMENT_SURVEY_TOKEN,
    GET_SURVEY,
    GET_SURVEY_AGES,
    GET_SURVEY_AGES_FAILURE,
    GET_SURVEY_AGES_SUCCESS,
    GET_SURVEY_GENDERS,
    GET_SURVEY_GENDERS_FAILURE,
    GET_SURVEY_GENDERS_SUCCESS,
    GET_SURVEY_OPTIONS,
    GET_SURVEY_OPTIONS_FAIL,
    GET_SURVEY_OPTIONS_SUCCESS,
    GET_SURVEY_RESULT,
    GO_TO_PAGE,
    RESPONDENT_NAME,
    START_SURVEY,
    UPDATE_RESPONDENT_DEMOGRAPHICS,
    UPDATE_RESPONDENT_DEMOGRAPHICS_FAILURE,
    UPDATE_RESPONDENT_DEMOGRAPHICS_SUCCESS
} from 'constants/actionTypes';
import { ENGAGEMENT, EXPIRED_ENGAGEMENT, MOTIVATOR, PRE_ENGAGEMENT } from 'constants/pageDictionary';
import { fromJS, Map } from 'immutable';
import React from 'react';
import { changeLanguage } from 'actions/changeLanguageActions';
import { JAPANESE } from '../constants/language';
import { getAccountLogo } from '../actions/logoActions';
import { startTimer } from '../actions/timerActions';
import { attunedApi } from '../utils/api';
import {
    RESPONDENTS_DEMOGRAPHICS_URL,
    SURVEY_AGES_URL,
    SURVEY_GENDERS_URL,
    SURVEY_OPTIONS_URL
} from '../constants/api';
import { Question, ResultModel } from '../models';
import { getBrowserLanguage, isMobileDevice, parseErrorMessage } from '../utils/index';
import { loadI18nCustomization } from './customizationActions';

const getSurveySuccess = (surveys) => ({
    type: GET_SURVEY,
    surveys
});

const autoSaveSuccess = (status, listQSChanged) => ({
    type: AUTO_SAVE_SUCCESS,
    status,
    listQSChanged
});

const autoSaveFail = (errorData) => ({
    type: AUTO_SAVE_FAIL,
    errorData
});

/**
 * ATTUNED-2839
 */
const initialSurveyBatchClosed = (parsedError, motivatorToken, browserLanguage) => {
    const reportLink = `${ process.env.ATTUNED_URL }/report/motivator/${ motivatorToken }`;

    let reportLinkText;
    if (browserLanguage === '日本語') {
        reportLinkText = 'モチベーターレポートを見る';
    } else if (browserLanguage === 'Español') {
        reportLinkText = 'Ir a Mi Informe Motivador';
    } else {
        reportLinkText = 'Go to My Motivator Report';
    }

    return {
        type: GO_TO_PAGE,
        pageName: EXPIRED_ENGAGEMENT,
        pageProps: {
            message: parsedError.message,
            renderLink: () => {
                return (
                    <React.Fragment>
                        <br />
                        <br />
                        <a href={ reportLink }>{ reportLinkText }</a>
                    </React.Fragment>
                );
            }
        }
    };
};

const batchClosed = (parsedError, browserLanguage) => {
    let redirectToText;
    if (browserLanguage === '日本語') {
        redirectToText = '回答開始';
    } else if (browserLanguage === 'Español') {
        redirectToText = 'Comenzar la encuesta';
    } else {
        redirectToText = 'Start Survey';
    }
    return {
        type: GO_TO_PAGE,
        pageName: EXPIRED_ENGAGEMENT,
        pageProps: {
            message: parsedError.message,
            renderLink: () => {
                return (
                    <React.Fragment>
                        <br />
                        <br />
                        <a href={ `/engagement/${ parsedError.redirect_to }` }>{ redirectToText }</a>
                    </React.Fragment>
                );
            }
        }
    };
};

const engagementSurveyDeleted = (parsedError, browserLanguage) => {
    let errorPrefix;
    if (browserLanguage === '日本語') {
        errorPrefix = 'ページを表示できません。';
    } else if (browserLanguage === 'Español') {
        errorPrefix = 'Lo sentimos, esta página no está disponible.';
    } else {
        errorPrefix = "Sorry, this page isn't available.";
    }
    return {
        type: GO_TO_PAGE,
        pageName: EXPIRED_ENGAGEMENT,
        pageProps: {
            message: `${ errorPrefix }\n${ parsedError.message }`
        }
    };
};

const handleQuestion = (questionList, status, lastEngagementQuestion, directQuestions) => {
    return Map({
        status,
        likert: questionList.map(item => new Question(item)),
        directQuestions: directQuestions.map(item => new Question(item)),
        lastEngagementQuestion
    });
};

export const getSurvey = (type, token) => {
    const URL = `/${ type }/${ token }`;
    return dispatch => {
        if (window.location.href.indexOf('share=true') < 0) {
            return attunedApi.get(URL)
                .then(response => {
                    const {
                        respondentFirstName,
                        respondentId,
                        respondentRoleId,
                        respondentLastName,
                        respondentRoleName,
                        startedDate,
                        surveyId,
                        has_engagement_module,
                        languageId,
                        userRoleId,
                        userRole,
                        questionList,
                        is_retake,
                        has_pending_batch_survey,
                        respondent_email
                    } = response;
                    const accountId = response.account_id;
                    const languageName = response.languageName || response.language_name || 'English';
                    const optimizeQs = handleQuestion(fromJS(questionList), response.status, response.lastQuestion, fromJS(response.directQuestionsList || []));
                    const lastQuestion = questionList.length - questionList
                        .slice()
                        .reverse()
                        .findIndex(item => item.optionId >= 0 && item.optionId <= 6);
                    dispatch(getAccountLogo(accountId));
                    dispatch(getSurveySuccess(optimizeQs));
                    dispatch({
                        type: RESPONDENT_NAME,
                        respondentId,
                        respondentRoleId,
                        respondentRoleName,
                        firstName: respondentFirstName,
                        languageId,
                        lastName: respondentLastName,
                        surveyId,
                        userRoleId,
                        userRole,
                        lastQuestion,
                        questionList,
                        accountId,
                        is_retake,
                        has_pending_batch_survey,
                        has_engagement_module,
                        respondent_email
                    });
                    dispatch(changeLanguage(languageName));
                    dispatch(startTimer(startedDate));

                    if (languageName === JAPANESE) {
                        return dispatch(loadI18nCustomization(accountId));
                    }
                    return Promise.resolve();
                }).catch((err) => {
                    const path = window.location.pathname.split('/');
                    const motivatorToken = path[path.length - 1];
                    const parsedError = parseErrorMessage(err);
                    const browserLanguage = getBrowserLanguage();
                    const accountId = err.account_id;
                    const respondentSurveyId = err.respondent_survey_id;

                    if (accountId) {
                        dispatch(getAccountLogo(accountId));
                    }

                    if (err.error === 'NEW_MOTIVATOR_SURVEY_AVAILABLE') {
                        if (respondentSurveyId) {
                            window.location.href = `/${ respondentSurveyId }`;
                            return;
                        }
                    }

                    if (err.error === 'CANDIDATES_CANNOT_HAVE_ENGAGEMENT') {
                        // ATTUNED-3192: The member will arrive here both if they tried to respond to
                        // an engagement survey, and if they clicked on the assessment link in the email.
                        if (respondentSurveyId) {
                            window.location.href = `${ process.env.ATTUNED_URL }/report/motivator/${ respondentSurveyId }`;
                            return;
                        }
                    }

                    if (err.error === 'MOTIVATOR_SURVEY_COMPLETED') {
                        if (!err.has_engagement_module) {
                            // ATTUNED-3090
                            window.location.href = `${ process.env.ATTUNED_URL }/report/motivator/${ motivatorToken }`;
                            return;
                        }

                        if (err.tokenId) {
                            dispatch({
                                type: ENGAGEMENT_SURVEY_TOKEN,
                                engagementSurveyToken: err.tokenId
                            });
                            dispatch({
                                type: RESPONDENT_NAME,
                                firstName: err.respondentFirstName,
                                lastName: err.respondentLastName,
                                surveyId: err.surveyId,
                                languageId: err.languageId,
                                respondentRoleId: err.respondentRoleId,
                                accountId: err.account_id
                            });
                            dispatch({ type: GO_TO_PAGE, pageName: PRE_ENGAGEMENT });
                            return;
                        }
                    }

                    if (err.error === 'ENGAGEMENT_SURVEY_COMPLETED' || err.message === 'Survey done') {
                        if (type === 'survey') {
                            // eslint-disable-next-line consistent-return
                            return window.location.href = `${ process.env.ATTUNED_URL }/report/motivator/${ motivatorToken }`;
                        }
                        dispatch({
                            type: GO_TO_PAGE,
                            pageName: EXPIRED_ENGAGEMENT,
                            pageProps: {
                                message: err.error
                            }
                        });
                        throw err;
                    }

                    // ATTUNED-2839
                    if (err.error === 'INITIAL_SURVEY_BATCH_CLOSED') {
                        const latestRespondentSurveyId = err.respondent_survey_id;
                        dispatch(initialSurveyBatchClosed(parsedError, latestRespondentSurveyId || motivatorToken, browserLanguage));
                        return;
                    }

                    if (err.error === 'BATCH_CLOSED') {
                        dispatch(batchClosed(parsedError, browserLanguage));
                        return;
                    }

                    if (err.error === 'ENGAGEMENT_SURVEY_DELETED_RETAKE' || err.error === 'ENGAGEMENT_SURVEY_EXPIRED') {
                        dispatch(engagementSurveyDeleted(parsedError, browserLanguage));
                        return;
                    }

                    if (err.error === 'INVALID_SESSION') {
                        dispatch({
                            type: GO_TO_PAGE,
                            pageName: EXPIRED_ENGAGEMENT,
                            pageProps: {
                                message: 'LOGIN_REDIRECT'
                            }
                        });
                        return;
                    }

                    // ATTUNED-3192
                    if (err.error === 'NO_ENGAGEMENT_MODULE' || err.error === 'LICENSE_EXPIRED' || err.error === 'INACTIVE_RESPONDENT' || err.error === 'ACCESS_DENIED') {
                        dispatch({
                            type: GO_TO_PAGE,
                            pageName: EXPIRED_ENGAGEMENT,
                            pageProps: {
                                message: err.error
                            }
                        });
                        return;
                    }

                    // MOTIVATOR_SURVEY_NOT_FOUND, ENGAGEMENT_SURVEY_NOT_FOUND, RESPONDENT_NOT_FOUND will also end up here
                    if (err) {
                        dispatch({
                            type: GO_TO_PAGE,
                            pageName: EXPIRED_ENGAGEMENT,
                            pageProps: {
                                message: 'welcome.the_link_you_clicked_may_be_broken',
                                title: 'welcome.page_not_found'
                            }
                        });
                    }
                });
        }

        return new Promise((resolve) => {
            const path = window.location.pathname.split('/');
            const motivatorToken = path[path.length - 1];
            window.location.href = `${ process.env.ATTUNED_URL }/report/motivator/${ motivatorToken }`;
            resolve();
        });
    };
};

export const goToSurveyPage = ({ lastQuestion, questionList, surveyId }) => {
    return dispatch => {
        if (Number.isInteger(lastQuestion) && lastQuestion <= questionList.length) {
            const pageName = surveyId === 5 ? ENGAGEMENT : MOTIVATOR;
            dispatch({
                type: GO_TO_PAGE,
                pageName
            });
        } else if (surveyId === 5) {
            dispatch({
                type: GO_TO_PAGE,
                pageName: ENGAGEMENT
            });
        }
    };
};

export const autoSave = ({
    survey, isSubmit, surveyId, type, token = null
}) => {
    const path = window.location.pathname.split('/');
    const urlToken = path[path.length - 1];
    const url = `/${ type }`;

    const answers = survey.map(question => {
        let qs = question;
        if (qs.get) {
            qs = new Question(qs);
        }

        return {
            question_id: qs.id,
            option_value: qs.optionId,
            question_type: qs.type,
            comment: qs.comment
        };
    });
    return dispatch => {
        return attunedApi({
            method: 'PUT',
            url,
            headers: { 'Content-Type': 'application/json' },
            data: {
                token: token !== null ? token : urlToken,
                answers,
                submit: isSubmit,
                surveyId,
                device: isMobileDevice() ? 'Mobile' : 'Desktop'
            }
        }).then(response => {
            dispatch(autoSaveSuccess(response.status, survey));
            if (surveyId === 4 && isSubmit) {
                dispatch({
                    type: ENGAGEMENT_SURVEY_TOKEN, engagementSurveyToken: response.tokenId
                });
            }
            return response;
        }).catch(error => {
            const browserLanguage = getBrowserLanguage();
            const parsedError = parseErrorMessage(error);

            if (type === 'engagement/answer/') {
                const isExpireLink = error.response.link_expire;
                if (isExpireLink) {
                    return true;
                }
                return false;
            }

            if (error.error === 'BATCH_CLOSED') {
                dispatch(batchClosed(parsedError, browserLanguage));
                return false;
            }

            if (error.error === 'INITIAL_SURVEY_BATCH_CLOSED') {
                const latestRespondentSurveyId = error.respondent_survey_id;
                const motivatorToken = path[path.length - 1];
                dispatch(initialSurveyBatchClosed(parsedError, latestRespondentSurveyId || motivatorToken, browserLanguage));
                return false;
            }

            if (error.error === 'ENGAGEMENT_SURVEY_COMPLETED') {
                window.location.reload(true);
                return false;
            }

            dispatch(autoSaveFail(parsedError.message));
            return false;
        });
    };
};

export const startSurvey = () => {
    const url = '/survey/start';
    const path = window.location.pathname.split('/');
    const token = path[path.length - 1];
    return dispatch => {
        dispatch({ type: START_SURVEY });
        return attunedApi({
            method: 'PUT',
            url,
            headers: { 'Content-Type': 'application/json' },
            data: { 'id': token }
        });
    };
};

export const getResult = () => {
    const path = window.location.pathname.split('/');
    const token = path[1];

    const URL = `/survey/result/${ token }`;
    return dispatch => attunedApi.get(URL)
        .then(response => {
            const {
                respondent_first_name, respondent_last_name,
                language_id,
                result,
                self_motivator
            } = response;
            dispatch({
                type: RESPONDENT_NAME,
                languageId: language_id,
                firstName: respondent_first_name,
                lastName: respondent_last_name
            });
            dispatch({
                type: GET_SURVEY_RESULT,
                result: {
                    selfMotivator: self_motivator,
                    result: result.map(item => new ResultModel(item))
                }
            });
        });
};

export const getLink = (type, content) => {
    const URL = '/share-motivator-result';
    return () => attunedApi.post(URL, { url: `${ window.location.href }?share=true&type=${ type }`, content })
        .then(response => response);
};

export const getSurveyOptions = engagementSurveyToken => {
    const URL = `${ SURVEY_OPTIONS_URL }${ engagementSurveyToken }`;
    return (dispatch) => {
        dispatch({ type: GET_SURVEY_OPTIONS });
        return attunedApi.get(URL)
            .then(response => {
                dispatch({ type: GET_SURVEY_OPTIONS_SUCCESS, payload: response });
            })
            .catch(err => {
                dispatch({ type: GET_SURVEY_OPTIONS_FAIL, payload: err });
                throw err;
            });
    };
};

export const getRespondentAges = () => {
    const URL = `${ SURVEY_AGES_URL }`;
    return (dispatch) => {
        dispatch({ type: GET_SURVEY_AGES });
        return attunedApi.get(URL)
            .then(response => {
                dispatch({ type: GET_SURVEY_AGES_SUCCESS, payload: response });
            })
            .catch(err => {
                dispatch({ type: GET_SURVEY_AGES_FAILURE, payload: err });
                throw err;
            });
    };
};

export const getRespondentGenders = () => {
    const URL = `${ SURVEY_GENDERS_URL }`;
    return (dispatch) => {
        dispatch({ type: GET_SURVEY_GENDERS });
        return attunedApi.get(URL)
            .then(response => {
                dispatch({ type: GET_SURVEY_GENDERS_SUCCESS, payload: response });
            })
            .catch(err => {
                dispatch({ type: GET_SURVEY_GENDERS_FAILURE, payload: err });
                throw err;
            });
    };
};

export const updateRespondent = (payload) => {
    const URL = `${ RESPONDENTS_DEMOGRAPHICS_URL }`;
    return (dispatch) => {
        dispatch({ type: UPDATE_RESPONDENT_DEMOGRAPHICS });
        return attunedApi.put(URL, { ...payload })
            .then(response => {
                dispatch({ type: UPDATE_RESPONDENT_DEMOGRAPHICS_SUCCESS, payload: response });
            })
            .catch(err => {
                dispatch({ type: UPDATE_RESPONDENT_DEMOGRAPHICS_FAILURE, payload: err });
                throw err;
            });
    };
};

export const updateEngagementSurveyToken = token => {
    return dispatch => {
        dispatch({
            type: ENGAGEMENT_SURVEY_TOKEN,
            engagementSurveyToken: token
        });
    };
};
